import { Form, Input, Spin } from 'antd';
import RadioCards from 'components/RadioCards/RadioCards';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { familyService } from 'services';
import {
  FAMILY_APPLICATION_STEPS,
  CHILD_RELATIONSHIP_OPTIONS,
  CHILD_RELATIONSHIP_OPTIONS_MAP,
} from '../../familyApplicationConstants';
import PageMeta from 'components/PageMeta/PageMeta';
import { getNextStep, getPreviousStep, isChildSchoolAge, stepConfigAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { useLocation } from 'react-router-dom';
import { DatePicker } from 'components/ui/DatePicker';
import dayjs from 'dayjs';
import { DATE_FORMAT_BACKEND } from 'components/AriaDatePicker/AriaDatePicker';
import { Banner } from 'components/ui/Banner';
import FormHelperText from 'components/ui/FormHelperText/FormHelperText';
import { getFirstName, getOrdinalString } from 'utils';
import { getApplicantFirstName } from '../../utils';
import { FAMILY_BRANCH_MAP } from 'containers/Family/AddApplication2/familyApplicationConstants';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';

export function FamilyApplicationChildrenBasicInfo({
  application,
  setApplication,
  household,
  loading,
  setStep,
  readOnly = false,
}) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const stepConfig = useAtomValue(stepConfigAtom);
  const location = useLocation();

  // Get entity index from query parameters
  const urlParams = new URLSearchParams(location.search);
  const currentChildIndex = parseInt(urlParams.get('entity') || '0', 10);

  const isCompletedBySponsor = application?.completedBySponsor === true;

  // Watch form values with FormValue prefix
  const firstNameFormValue = Form.useWatch('firstName', form);
  const lastNameFormValue = Form.useWatch('lastName', form);
  const dateOfBirthFormValue = Form.useWatch('dateOfBirth', form);
  const relationToApplicantFormValue = Form.useWatch('relationToApplicant', form);

  // Check if basic info is complete to show relationship question
  const isBasicInfoComplete = firstNameFormValue && lastNameFormValue && dateOfBirthFormValue;

  // Check if relationship is selected to show birth certificate question
  const isRelationshipSelected = Boolean(relationToApplicantFormValue);

  const currentChild = application?.children?.[currentChildIndex];
  const isEditing = Boolean(currentChild?.id);

  const showSponsorOnBirthCertificateQuestion = useMemo(() => {
    return (
      isRelationshipSelected &&
      relationToApplicantFormValue === CHILD_RELATIONSHIP_OPTIONS_MAP.child.value &&
      lastNameFormValue?.toLowerCase?.() !== application?.applicant?.lastName?.toLowerCase?.() &&
      application?.applicant?.militaryDetails?.branch?.id === FAMILY_BRANCH_MAP.army.id
    );
  }, [
    isRelationshipSelected,
    relationToApplicantFormValue,
    lastNameFormValue,
    application?.applicant?.lastName,
    application?.applicant?.militaryDetails?.branch?.id,
  ]);

  // Check if we should show the additional adult relationship question
  const showAdditionalAdultRelationshipQuestion = useMemo(() => {
    return Boolean(application?.additionalParents?.[0]);
  }, [application?.additionalParents]);

  useEffect(() => {
    if (currentChild) {
      form.setFieldsValue({
        firstName: currentChild.firstName,
        lastName: currentChild.lastName,
        dateOfBirth: currentChild.dateOfBirth ? new Date(currentChild.dateOfBirth) : null,
        relationToApplicant: currentChild.relationToApplicant,
        applicantOnBirthCertificate: currentChild.applicantOnBirthCertificate,
        relationToAdditionalAdult: currentChild.relationToAdditionalAdult,
      });
    }
  }, [currentChild, form]);

  const handleSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        // Navigate to next step
        const nextStep = getNextStep(
          FAMILY_APPLICATION_STEPS.CHILDREN_BASIC_INFO,
          _newApplication,
          stepConfig,
          currentChildIndex,
        );
        setStep({
          applicationId: _newApplication.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        const newDateOfBirth = values.dateOfBirth ? dayjs(values.dateOfBirth).format(DATE_FORMAT_BACKEND) : null;
        const childData = {
          ...currentChild,
          firstName: values.firstName,
          lastName: values.lastName,
          dateOfBirth: newDateOfBirth,
          relationToApplicant: values.relationToApplicant,
          applicantOnBirthCertificate: values.applicantOnBirthCertificate,
          relationToAdditionalAdult: values.relationToAdditionalAdult,
          ...(!isChildSchoolAge({ dateOfBirth: newDateOfBirth }) ? { inSchool: null } : {}),
        };

        // Step 1: Add/update child to household using household endpoint
        const addUpdateChildResponse = await familyService.addUpdateFamilyChild(
          household?.id,
          currentChild?.id || null,
          currentChild?.id !== undefined
            ? {
                ...currentChild,
                ...childData,
              }
            : [
                {
                  ...currentChild,
                  ...childData,
                },
              ],
        );
        const savedChild = addUpdateChildResponse.id ? addUpdateChildResponse : addUpdateChildResponse[0];
        if (application?.children?.findIndex((child) => child.id === savedChild.id) === -1) {
          await familyService.saveChild(application?.id, savedChild);
        }

        // FAMILY-APPS__DATA_CLEANING - Clear eligibility certification if child's key fields change
        const childChanged =
          currentChild?.firstName !== values.firstName ||
          currentChild?.lastName !== values.lastName ||
          currentChild?.dateOfBirth !== newDateOfBirth ||
          currentChild?.relationToApplicant !== values.relationToApplicant;

        // Step 2: Link child to application using application endpoint
        const updatedApplication = {
          ...application,
          children: [...(application.children || []), savedChild],
          // Clear eligibility certification fields if child data changed
          ...(childChanged && {
            selfCertCompletedBySponsor: null,
            selfcertAccepted: null,
            selfcertAcceptedBy: null,
          }),
        };
        const savedApplication = await familyService.saveApplication(updatedApplication);
        setApplication(savedApplication);

        _goToNextStep(savedApplication);
      } catch (error) {
        console.error('Error saving child basic info:', error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, currentChild, currentChildIndex, household?.id, readOnly, setApplication, setStep, stepConfig],
  );

  const childFirstName = getFirstName({ firstName: currentChild?.firstName });

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={submitting}>
      <PageMeta title={`${childFirstName} - Basic Information`} />

      <Form form={form} onFinish={handleSubmit} layout="vertical" disabled={readOnly}>
        <div className="p-7 pt-0">
          <div className="space-y-8">
            <Banner
              question={isEditing ? `Edit ${childFirstName}'s information` : "Let's add your children who need care"}
              guidance={isEditing ? '' : "We'll go through them one at a time"}
              className="my-12"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Form.Item
                  name="firstName"
                  label={isEditing ? 'First name' : `${getOrdinalString(currentChildIndex + 1)} child's name`}
                  rules={[
                    { required: true, message: "Please enter the child's first name" },
                    { max: 50, message: 'First name cannot exceed 50 characters' },
                  ]}
                  className="mb-0"
                >
                  <Input data-testid="child-first-name-input" />
                </Form.Item>

                <FormHelperText text="As shown on birth certificate" />
              </div>

              <Form.Item
                name="lastName"
                label="Last name"
                rules={[
                  { required: true, message: "Please enter the child's last name" },
                  { max: 50, message: 'Last name cannot exceed 50 characters' },
                ]}
              >
                <Input data-testid="child-last-name-input" />
              </Form.Item>
            </div>

            <Form.Item
              name="dateOfBirth"
              label="Date of birth"
              rules={[
                { required: true, message: "Please select the child's date of birth" },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    if (dayjs().diff(value, 'years') >= 18) {
                      return Promise.reject(new Error('Child must be under 18 years old'));
                    }

                    if (dayjs(value).isAfter(dayjs())) {
                      return Promise.reject(new Error('Date of birth cannot be in the future'));
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <DatePicker data-testid="child-date-of-birth-picker" />
            </Form.Item>

            {isBasicInfoComplete && (
              <Form.Item
                name="relationToApplicant"
                label={
                  isCompletedBySponsor
                    ? `How is ${getFirstName({ firstName: firstNameFormValue })} related to you?`
                    : `How is ${getFirstName({ firstName: firstNameFormValue })} related to ${getApplicantFirstName(
                        application?.applicant,
                      )}?`
                }
                rules={[{ required: true, message: 'Please select the relationship to the Service Member' }]}
              >
                <RadioCards
                  options={CHILD_RELATIONSHIP_OPTIONS.map((option) => ({
                    value: option.value,
                    label: option.title,
                  }))}
                  data-testid="child-relationship-cards"
                />
              </Form.Item>
            )}

            {showSponsorOnBirthCertificateQuestion && (
              <div className="!mt-10">
                <Form.Item
                  name="applicantOnBirthCertificate"
                  label={
                    isCompletedBySponsor
                      ? 'Are you listed on the birth certificate?'
                      : 'Is the service member listed on the birth certificate?'
                  }
                  rules={[
                    {
                      required: true,
                      message: isCompletedBySponsor
                        ? 'Please indicate if you are on the birth certificate'
                        : 'Please indicate if the service member is on the birth certificate',
                    },
                  ]}
                >
                  <RadioCards
                    options={[
                      { value: true, label: 'Yes' },
                      { value: false, label: 'No' },
                    ]}
                    data-testid="sponsor-on-birth-certificate-radio"
                  />
                </Form.Item>
              </div>
            )}

            {showAdditionalAdultRelationshipQuestion && (
              <div className="!mt-10">
                <Form.Item
                  name="relationToAdditionalAdult"
                  label={
                    isCompletedBySponsor
                      ? `How is ${getFirstName({ firstName: firstNameFormValue })} related to ${getFirstName({
                          firstName: application?.additionalParents?.[0]?.firstName,
                        })}?`
                      : `How is ${getFirstName({ firstName: firstNameFormValue })} related to you?`
                  }
                  rules={[
                    {
                      required: true,
                      message: `Please select the relationship to ${
                        isCompletedBySponsor
                          ? getFirstName({
                              firstName: application?.additionalParents?.[0]?.firstName,
                            })
                          : 'you'
                      }`,
                    },
                  ]}
                >
                  <RadioCards
                    options={CHILD_RELATIONSHIP_OPTIONS.map((option) => ({
                      value: option.value,
                      label: option.title,
                    }))}
                    data-testid="child-additional-adult-relationship-cards"
                  />
                </Form.Item>
              </div>
            )}
          </div>
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            onClick: () => {
              const previousStep = getPreviousStep(
                FAMILY_APPLICATION_STEPS.CHILDREN_BASIC_INFO,
                application,
                stepConfig,
                currentChildIndex === 0 ? null : currentChildIndex,
              );
              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
            disabled: submitting,
            'data-testid': 'children-basic-info-back-button',
          }}
          nextButtonProps={{
            loading: submitting,
            htmlType: 'submit',
            'data-testid': 'children-basic-info-next-button',
          }}
        />
      </Form>
    </Spin>
  );
}
